/**
 * Audio recording hook for voice feedback.
 *
 * Captures microphone audio and returns it as a Blob for upload.
 * Simpler than useAudioCapture - designed for recording short clips
 * rather than streaming.
 */

import { useRef, useState, useCallback } from 'react'

export interface AudioRecordingState {
  /** Whether recording is currently in progress */
  isRecording: boolean
  /** The recorded audio blob (null until recording stops) */
  audioBlob: Blob | null
  /** Error message if recording failed */
  error: string | null
}

export interface AudioRecordingControls {
  /** Start recording from the microphone */
  startRecording: () => Promise<void>
  /** Stop recording and produce the audio blob */
  stopRecording: () => Promise<void>
  /** Clear the recorded audio */
  clearRecording: () => void
}

// Preferred MIME types in order of preference
const MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
  'audio/wav',
]

/**
 * Get the best supported audio MIME type for MediaRecorder.
 */
function getSupportedMimeType(): string {
  for (const type of MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type
    }
  }
  return '' // Let browser decide
}

/**
 * Hook for recording audio feedback.
 *
 * @example
 * ```tsx
 * const { isRecording, audioBlob, startRecording, stopRecording, clearRecording } = useAudioRecording()
 *
 * // Start recording
 * await startRecording()
 *
 * // Stop and get blob
 * await stopRecording()
 * if (audioBlob) {
 *   const formData = new FormData()
 *   formData.append('audio', audioBlob)
 *   // upload...
 * }
 *
 * // Clear the recording
 * clearRecording()
 * ```
 */
export function useAudioRecording(): AudioRecordingState & AudioRecordingControls {
  const [isRecording, setIsRecording] = useState(false)
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const streamRef = useRef<MediaStream | null>(null)

  const startRecording = useCallback(async () => {
    if (isRecording) return

    try {
      setError(null)
      setAudioBlob(null)
      chunksRef.current = []

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })
      streamRef.current = stream

      // Create MediaRecorder with best available codec
      const mimeType = getSupportedMimeType()
      const options: MediaRecorderOptions = mimeType ? { mimeType } : {}
      const recorder = new MediaRecorder(stream, options)
      mediaRecorderRef.current = recorder

      // Collect audio chunks
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data)
        }
      }

      // Start recording with 100ms chunks for smoother progress
      recorder.start(100)
      setIsRecording(true)

    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setError(`Failed to start recording: ${message}`)
      cleanup()
    }
  }, [isRecording])

  const stopRecording = useCallback(async () => {
    return new Promise<void>((resolve) => {
      const recorder = mediaRecorderRef.current
      if (!recorder || recorder.state === 'inactive') {
        resolve()
        return
      }

      recorder.onstop = () => {
        // Create blob from collected chunks
        const mimeType = recorder.mimeType || 'audio/webm'
        const blob = new Blob(chunksRef.current, { type: mimeType })
        setAudioBlob(blob)
        setIsRecording(false)
        cleanup()
        resolve()
      }

      recorder.stop()
    })
  }, [])

  const clearRecording = useCallback(() => {
    setAudioBlob(null)
    setError(null)
    chunksRef.current = []
  }, [])

  const cleanup = useCallback(() => {
    // Stop all tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    mediaRecorderRef.current = null
  }, [])

  return {
    isRecording,
    audioBlob,
    error,
    startRecording,
    stopRecording,
    clearRecording,
  }
}

export default useAudioRecording
